import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { searchApi } from '../../api/search';
import type { GlobalSearchResponseDto, GlobalSearchItemDto } from '../../api/types';
import { useSnackbar } from '../../context/SnackbarContext';

function useDebounce<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

interface GlobalSearchModalProps {
  open: boolean;
  onClose: () => void;
}

const TYPE_LABEL: Record<string, string> = {
  WORKSITE: 'Commessa',
  CUSTOMER: 'Cliente',
  REPORT: 'Rapportino',
  MILESTONE: 'Milestone',
};

export function GlobalSearchModal({ open, onClose }: GlobalSearchModalProps) {
  const navigate = useNavigate();
  const { showMessage } = useSnackbar();
  const [q, setQ] = useState('');
  const debouncedQ = useDebounce(q, 280);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<GlobalSearchResponseDto | null>(null);

  useEffect(() => {
    if (!open) {
      setQ('');
      setData(null);
      return;
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const t = debouncedQ.trim();
    if (t.length < 2) {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    searchApi
      .global(t)
      .then(setData)
      .catch(() => showMessage('Ricerca non disponibile.', 'error'))
      .finally(() => setLoading(false));
  }, [open, debouncedQ, showMessage]);

  const go = useCallback(
    (item: GlobalSearchItemDto) => {
      onClose();
      navigate(item.targetUrl);
    },
    [navigate, onClose],
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" scroll="paper">
      <DialogTitle>Ricerca globale</DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        <TextField
          autoFocus
          fullWidth
          size="small"
          placeholder="Almeno 2 caratteri (commesse, clienti, rapportini, milestone)…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} fontSize="small" />,
          }}
          sx={{ mb: 2 }}
        />
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          Clienti: ricerca su nome / email / telefono (come nel CRM). Partita IVA non indicizzata nel modello
          attuale.
        </Typography>

        {loading && (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!loading && debouncedQ.trim().length >= 2 && data && data.totalCount === 0 && (
          <Typography color="text.secondary">Nessun risultato.</Typography>
        )}

        {!loading && data && data.sections.length > 0 && (
          <List dense disablePadding>
            {data.sections.map((sec) => (
              <Box key={sec.type} sx={{ mb: 1 }}>
                <Typography variant="overline" color="text.secondary" sx={{ pl: 1 }}>
                  {sec.label}
                </Typography>
                <Divider sx={{ my: 0.5 }} />
                {sec.items.map((item) => (
                  <ListItemButton key={`${item.type}-${item.id}`} onClick={() => go(item)} sx={{ borderRadius: 1 }}>
                    <ListItemText
                      primary={item.title}
                      secondary={
                        <>
                          {item.subtitle && (
                            <Typography component="span" variant="body2" color="text.secondary" display="block">
                              {item.subtitle}
                            </Typography>
                          )}
                          <Typography component="span" variant="caption" color="text.disabled">
                            {TYPE_LABEL[item.type] ?? item.type}
                            {item.status ? ` · ${item.status}` : ''}
                          </Typography>
                        </>
                      }
                    />
                  </ListItemButton>
                ))}
              </Box>
            ))}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
}
